import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Edit, CheckCircle, XCircle, Loader2, FileText, Package, Clock, User, Truck } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import ProcurementService from '@/api/procurement.service';
import { canEdit } from '@/lib/permissions';

const GoodsReceivedDetail: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [goodsReceived, setGoodsReceived] = useState<any>(null);
  const [isUpdating, setIsUpdating] = useState(false);

  const businessId = parseInt(localStorage.getItem('businessId') || '1');

  useEffect(() => {
    if (id) {
      fetchGoodsReceived();
    }
  }, [id]);

  const fetchGoodsReceived = async () => {
    try {
      setIsLoading(true);
      const response = await ProcurementService.getGoodsReceivedVoucher(id!);
      
      if (response.data?.success) {
        setGoodsReceived(response.data.data);
      } else {
        toast({
          variant: "destructive",
          title: "Error",
          description: "Failed to load goods received voucher",
        });
        navigate('/procurement/goods-received');
      }
    } catch (error: any) {
      console.error('Error fetching goods received voucher:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to load goods received voucher",
      });
      navigate('/procurement/goods-received');
    } finally {
      setIsLoading(false);
    }
  };

  const handleStatusUpdate = async (newStatus: string, comment?: string) => {
    setIsUpdating(true);
    try {
      const response = await ProcurementService.updateGoodsReceivedVoucher(id!, { status: newStatus, comment });
      
      if (response.data?.success) {
        toast({
          title: "Success",
          description: `Goods received voucher ${newStatus.replace('_', ' ')} successfully`,
        });
        fetchGoodsReceived(); // Refresh data
      } else {
        toast({
          variant: "destructive",
          title: "Error",
          description: response.data?.message || "Failed to update status",
        });
      }
    } catch (error: any) {
      console.error('Error updating status:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to update status",
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      draft: { variant: 'secondary' as const, label: 'Draft' },
      pending_verification: { variant: 'outline' as const, label: 'Pending Verification' },
      verified: { variant: 'default' as const, label: 'Verified' },
      posted: { variant: 'default' as const, label: 'Posted' },
      rejected: { variant: 'destructive' as const, label: 'Rejected' }
    };

    const config = statusConfig[status as keyof typeof statusConfig] || { variant: 'secondary' as const, label: status };
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const getQualityStatusBadge = (status: string) => {
    const statusConfig = {
      pending: { variant: 'outline' as const, label: 'Pending' },
      passed: { variant: 'default' as const, label: 'Passed' },
      failed: { variant: 'destructive' as const, label: 'Failed' },
      partial: { variant: 'secondary' as const, label: 'Partial' }
    };

    const config = statusConfig[status as keyof typeof statusConfig] || { variant: 'outline' as const, label: status };
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const getTotalReceivedValue = () => {
    return goodsReceived?.items?.reduce((sum: number, item: any) => sum + (item.acceptedQuantity * item.unitPrice), 0) || 0;
  };

  const getTotalReceivedQuantity = () => {
    return goodsReceived?.items?.reduce((sum: number, item: any) => sum + item.receivedQuantity, 0) || 0;
  };

  const getTotalAcceptedQuantity = () => {
    return goodsReceived?.items?.reduce((sum: number, item: any) => sum + item.acceptedQuantity, 0) || 0;
  };

  const getTotalRejectedQuantity = () => {
    return goodsReceived?.items?.reduce((sum: number, item: any) => sum + item.rejectedQuantity, 0) || 0;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex items-center gap-2">
          <Loader2 className="h-6 w-6 animate-spin" />
          <span>Loading goods received voucher...</span>
        </div>
      </div>
    );
  }

  if (!goodsReceived) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <h3 className="text-lg font-semibold">Goods Received Voucher Not Found</h3>
          <p className="text-muted-foreground">The requested goods received voucher could not be found.</p>
          <Button 
            onClick={() => navigate('/procurement/goods-received')}
            className="mt-4"
          >
            Back to Goods Received
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/procurement/goods-received')}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Goods Received
          </Button>
        </div>

        <div className="flex items-center gap-2">
          {canEdit('goods_received') && (goodsReceived.status === 'draft' || goodsReceived.status === 'rejected') && (
            <Button
              onClick={() => navigate(`/procurement/goods-received/${id}/edit`)}
              className="flex items-center gap-2"
            >
              <Edit className="h-4 w-4" />
              Edit
            </Button>
          )}
          
          {canEdit('Procurement') && goodsReceived.status === 'pending_verification' && (
            <>
              <Button
                onClick={() => handleStatusUpdate('verified', 'Verified via system')}
                disabled={isUpdating}
                className="flex items-center gap-2"
              >
                <CheckCircle className="h-4 w-4" />
                Verify
              </Button>
              <Button
                variant="destructive"
                onClick={() => handleStatusUpdate('rejected', 'Rejected via system')}
                disabled={isUpdating}
                className="flex items-center gap-2"
              >
                <XCircle className="h-4 w-4" />
                Reject
              </Button>
            </>
          )}

          {goodsReceived.status === 'verified' && (
            <Button
              onClick={() => handleStatusUpdate('posted', 'Posted to inventory')}
              disabled={isUpdating}
              className="flex items-center gap-2"
            >
              <Package className="h-4 w-4" />
              Post to Inventory
            </Button>
          )}
        </div>
      </div>

      {/* GRV Header */}
      <div>
        <div className="flex items-center gap-4 mb-2">
          <h1 className="text-3xl font-bold tracking-tight">GRV #{goodsReceived.grvNumber}</h1>
          {getStatusBadge(goodsReceived.status)}
        </div>
        <p className="text-muted-foreground">
          Received on {new Date(goodsReceived.receivedDate).toLocaleDateString()} by {goodsReceived.receivedBy}
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Value</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatCurrency(getTotalReceivedValue())}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Items</CardTitle>
            <Package className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{goodsReceived.items?.length || 0}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Received Qty</CardTitle>
            <Truck className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{getTotalReceivedQuantity()}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Accepted Qty</CardTitle>
            <CheckCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{getTotalAcceptedQuantity()}</div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="items">Items</TabsTrigger>
          <TabsTrigger value="quality">Quality Control</TabsTrigger>
          <TabsTrigger value="verification">Verification History</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>GRV Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">GRV Number</label>
                    <p className="text-sm">{goodsReceived.grvNumber}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Received Date</label>
                    <p className="text-sm">{new Date(goodsReceived.receivedDate).toLocaleDateString()}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Received By</label>
                    <p className="text-sm">{goodsReceived.receivedBy}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Purchase Order</label>
                    <p className="text-sm">{goodsReceived.purchaseOrder?.orderNumber || 'N/A'}</p>
                  </div>
                  {goodsReceived.supplierDeliveryNote && (
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Delivery Note</label>
                      <p className="text-sm">{goodsReceived.supplierDeliveryNote}</p>
                    </div>
                  )}
                </div>
                {goodsReceived.transportDetails && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Transport Details</label>
                    <p className="text-sm">{goodsReceived.transportDetails}</p>
                  </div>
                )}
                {goodsReceived.notes && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Notes</label>
                    <p className="text-sm">{goodsReceived.notes}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Purchase Order Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">PO Number</label>
                  <p className="text-sm">{goodsReceived.purchaseOrder?.orderNumber || 'N/A'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Supplier</label>
                  <p className="text-sm">{goodsReceived.purchaseOrder?.supplier?.name || 'N/A'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Current Status</label>
                  <div className="mt-1">{getStatusBadge(goodsReceived.status)}</div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="items" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Received Items</CardTitle>
              <CardDescription>Items received from the purchase order</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Item</TableHead>
                    <TableHead>Ordered</TableHead>
                    <TableHead>Received</TableHead>
                    <TableHead>Accepted</TableHead>
                    <TableHead>Rejected</TableHead>
                    <TableHead>Unit Price</TableHead>
                    <TableHead>Value</TableHead>
                    <TableHead>Quality</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {goodsReceived.items?.map((item: any, index: number) => (
                    <TableRow key={index}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{item.itemName}</p>
                          <p className="text-sm text-muted-foreground">{item.description}</p>
                        </div>
                      </TableCell>
                      <TableCell>{item.orderedQuantity}</TableCell>
                      <TableCell>{item.receivedQuantity}</TableCell>
                      <TableCell>{item.acceptedQuantity}</TableCell>
                      <TableCell>{item.rejectedQuantity}</TableCell>
                      <TableCell>{formatCurrency(item.unitPrice)}</TableCell>
                      <TableCell>{formatCurrency(item.acceptedQuantity * item.unitPrice)}</TableCell>
                      <TableCell>{getQualityStatusBadge(item.qualityStatus)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              
              <div className="mt-4 space-y-2 border-t pt-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <span className="text-sm text-muted-foreground">Total Received:</span>
                    <p className="font-semibold">{getTotalReceivedQuantity()}</p>
                  </div>
                  <div>
                    <span className="text-sm text-muted-foreground">Total Accepted:</span>
                    <p className="font-semibold">{getTotalAcceptedQuantity()}</p>
                  </div>
                  <div>
                    <span className="text-sm text-muted-foreground">Total Rejected:</span>
                    <p className="font-semibold">{getTotalRejectedQuantity()}</p>
                  </div>
                  <div>
                    <span className="text-sm text-muted-foreground">Total Value:</span>
                    <p className="font-semibold">{formatCurrency(getTotalReceivedValue())}</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="quality" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Quality Control Information</CardTitle>
              <CardDescription>Quality checks and inspection details</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Quality Check Required</label>
                  <p className="text-sm">{goodsReceived.qualityCheckRequired ? 'Yes' : 'No'}</p>
                </div>
                {goodsReceived.qualityCheckRequired && (
                  <>
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Quality Check By</label>
                      <p className="text-sm">{goodsReceived.qualityCheckBy || 'Not assigned'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Quality Check Date</label>
                      <p className="text-sm">
                        {goodsReceived.qualityCheckDate ? 
                          new Date(goodsReceived.qualityCheckDate).toLocaleDateString() : 'Not completed'}
                      </p>
                    </div>
                  </>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="verification" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Verification History</CardTitle>
              <CardDescription>Track the verification progress of this GRV</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {goodsReceived.verificationHistory?.map((entry: any, index: number) => (
                  <div key={index} className="flex items-center gap-4 p-4 border rounded-lg">
                    <div className="flex-shrink-0">
                      {getStatusBadge(entry.status)}
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium">{entry.comment || `Status changed to ${entry.status}`}</p>
                      <p className="text-xs text-muted-foreground">
                        {new Date(entry.createdAt).toLocaleString()} by {entry.user?.name || entry.verifier || 'System'}
                      </p>
                    </div>
                  </div>
                )) || (
                  <p className="text-muted-foreground">No verification history available</p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default GoodsReceivedDetail; 